'use client';

import { LoaderProvider } from '@/modules/league/PickleballLoaderContext';
import LeagueChangeScoresPage from '@/app/(default)/(leagues)/leagues/[slug]/session/[sessionId]/matchdate/[matchDateId]/components/LeagueChangeScoresPage';
import LeagueRSVPPage from '@/app/(default)/(leagues)/leagues/[slug]/session/[sessionId]/matchdate/[matchDateId]/components/LeagueRSVPPage';
import PageLayout from '@/modules/league/new-components/PageLayout';
import Loader from '@pickleballinc/features/src/Loader/Loader';
import { notFound, permanentRedirect, useSearchParams } from 'next/navigation';
import { useGetGameDayHeaderQueryData } from '@/app/(default)/(leagues)/leagues/[slug]/session/[sessionId]/matchdate/[matchDateId]/queries';
import LeagueGroupsPage from '@/app/(default)/(leagues)/leagues/[slug]/session/[sessionId]/matchdate/[matchDateId]/components/LeagueGroupsPage';
import LeagueMatchesPage from '@/app/(default)/(leagues)/leagues/[slug]/session/[sessionId]/matchdate/[matchDateId]/components/LeagueMatchesPage';
import AddPlayersWrapper from '@/modules/league/new-components/add-players-components/AddPlayersWrapper';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { useGetUserPermissionsApp } from '@/hooks/app/useGetUserPermissionsApp';
import { useEffect } from 'react';
import { TotalPlayersInSessionProvider } from '@/modules/league/TotalPlayersInSession';

interface LeaguesWrapperProps {
  slug: string;
  sessionId: string;
  matchDateId: string;
}

export const LeaguesWrapper = ({ slug, sessionId, matchDateId }: LeaguesWrapperProps) => {
  const searchParams = useSearchParams();
  const sequenceNumberParam = searchParams?.get('sequenceNumber');
  const sequenceNumber = sequenceNumberParam ? parseInt(sequenceNumberParam) : undefined;

  const tab = searchParams?.get('tab') || 'rsvp';

  const { data, isLoading } = useGetGameDayHeaderQueryData(slug, sessionId, matchDateId);

  useEffect(() => {
    if (data?.header?.details?.traditionalLeague === true && tab !== 'matches') {
      permanentRedirect(
        `${process.env.NEXT_PUBLIC_PL_URL}/leagues/${slug}/session/${sessionId}/matchdate/${matchDateId}?tab=matches`
      );
    }
  }, [data]);

  const { hasPermission } = useAuth();

  const isSuperAdmin = hasPermission(Permission.SUPER_ADMIN);
  const isManager = useGetUserPermissionsApp(
    data?.leagueId || '',
    data?.userSession?.pbUuid || '',
    ['CanManageScores', 'CanManageLeague', 'CanManageLeagueSession', 'IsPrimary']
  );

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <Loader backgroundColor='bg-leagues-300/90' />
      </div>
    );
  }

  if (!data) {
    return notFound();
  }

  const isAuthorized = isSuperAdmin || isManager;

  const isRatingLeague = data.header?.details?.isRatingLeague;
  const lastSequenceNumber = data.rounds?.sequences?.at(-1)?.SequenceNumber || 1;
  const currentRatingSequenceNumber =
    isRatingLeague && data.rounds?.sequences?.length > 0 && !sequenceNumber
      ? lastSequenceNumber
      : sequenceNumber || 1;
  const currentSequenceNumber =
    data.header.details.rankingType === 'UpDown'
      ? data.header?.details.numberOfSequences > 1 && data.header?.details.DidGenerate2ndSequence
        ? sequenceNumber
          ? sequenceNumber
          : 2
        : 1
      : currentRatingSequenceNumber;

  return (
    <div className='relative w-full'>
      <LoaderProvider>
        <TotalPlayersInSessionProvider>
          <PageLayout
            headerContent={data.header}
            params={{ slug, sessionId, matchDateId }}
            user={data.userSession}
            currentSequenceNumber={currentSequenceNumber}
            leagueId={data.leagueId}
            isAuthorized={isAuthorized || false}
          />
          <AddPlayersWrapper params={{ slug, sessionId, matchDateId }} />

          {tab === 'rsvp' && (
            <LeagueRSVPPage
              params={{ slug, sessionId, matchDateId }}
              headerContent={data.header}
              isAuthorized={isAuthorized || false}
            />
          )}
        </TotalPlayersInSessionProvider>

        {tab === 'change-scores' && (
          <LeagueChangeScoresPage
            params={{ slug, sessionId, matchDateId }}
            headerContent={data.header}
            isAuthorized={isAuthorized || false}
          />
        )}

        {tab === 'groups' && (
          <LeagueGroupsPage
            headerContent={data.header}
            params={{ slug, sessionId, matchDateId }}
            currentSequenceNumber={currentSequenceNumber}
            isAuthorized={isAuthorized || false}
          />
        )}

        {tab === 'matches' && (
          <LeagueMatchesPage
            headerContent={data.header}
            params={{ slug, sessionId, matchDateId }}
            currentSequenceNumber={currentSequenceNumber}
            rounds={data.rounds}
            isAuthorized={isAuthorized || false}
          />
        )}
      </LoaderProvider>
    </div>
  );
};
